import { useContext } from 'react';
import { UseFormReturn } from 'react-hook-form';
import Hint, { toHintTone } from '@srm/ui/hint';
import { IDocumentHint } from '@models/Forms/IForms';
import { IFieldElem } from '@models/IFormData';
import { formulaTraceAttrs } from '@/context/DocumentFormulaTraceContext';
import { DocumentRuntimeContext } from '@/context/DocumentRuntimeContext';
import { EnsureExternalDataSourceLoaded } from '@/utils/externalDataSourceUtils';
import { sanitizeDocumentHtml } from '@/utils/sanitizeDocumentHtml';
import { useActivated, useFormulaDisplay, useFormulaState } from '@molecules/formbuilder/hooks';
import { postProcessTextDisplay } from '../Text/postProcessTextDisplay';
import './FormHint.scss';

export interface IFormHintProps {
    hint?: IDocumentHint;
    formMethods: UseFormReturn<any>;
    isEdit: boolean;
    isNew: boolean;
    fields: Record<string, IFieldElem>;
    ensureExternalDataSourceLoaded?: EnsureExternalDataSourceLoaded;
}

const FormHint = ({ hint, formMethods, isEdit, isNew, fields, ensureExternalDataSourceLoaded }: IFormHintProps) => {
    const runtime = useContext(DocumentRuntimeContext);
    const readOnlyPreview = runtime.mode === 'supportPreview';
    const { activated } = useActivated();
    const trace = hint?.id
        ? { targetId: hint.id, targetKind: 'field' as const, targetName: hint.title || hint.id }
        : undefined;
    const visibility = useFormulaState(hint?.visibilityRules, fields, formMethods, isEdit, isNew, activated, {
        fallback: true,
        ensureExternalDataSourceLoaded,
        trace: trace && { ...trace, ruleKind: 'visibility' },
    });
    const display = useFormulaDisplay(hint?.value, fields, formMethods, isEdit, isNew, activated, {
        fallback: hint?.value,
        postProcess: postProcessTextDisplay,
        ensureExternalDataSourceLoaded,
        trace: trace && { ...trace, ruleKind: 'formula' },
    });

    const message = display.value?.toString().trim() ?? '';
    const title = hint?.title?.trim();
    if (!hint || !visibility || (!message && !title)) return null;

    return (
        <div
            className="form-field form-hint"
            data-testid={hint.id ? `hint-${hint.id}` : undefined}
            {...formulaTraceAttrs(hint.id, 'field')}
        >
            <Hint
                className="form-hint__plate"
                tone={toHintTone(hint.tone)}
                maxWidth="none"
                title={
                    title ? (
                        <span dangerouslySetInnerHTML={{ __html: sanitizeDocumentHtml(title, readOnlyPreview) }} />
                    ) : undefined
                }
            >
                {message ? (
                    <div
                        className="form-hint__message"
                        dangerouslySetInnerHTML={{ __html: sanitizeDocumentHtml(message, readOnlyPreview) }}
                    />
                ) : undefined}
            </Hint>
        </div>
    );
};

export default FormHint;
